import { Grid, Card, CardHeader, CardContent, Button, Typography, Checkbox, Tooltip } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { Service } from '../../../models/masters/service';
import { useEffect, useState } from 'react';
import { SelectServicesDialog } from '../../services/select-services.dialog';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { servicesEditableColumns } from '../../services/grid-editable-columns';
import { FormGridEditable } from '../../../components/hook-form/form-grid-editable.component';
import { GridColumnConfig, GridPreference } from '../../../models/gridCoumnConfig';
import { useAuthorize } from '../../../hooks/use-authorize';
import { GridCellProps } from '@progress/kendo-react-grid';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import ServiceNotReadyFields from '../../services/service-not-ready-fields';
import { gridApiKeyStore } from '../../../api/grid-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../hooks/use-debounce';
import { array } from 'yup';

const SELECTED_FIELD = 'isSelected';

interface CustomGridCellProps {
  field: string;
  children: any[];
}

interface ServicesFormProps {
  name: string;
  page?: string;
  id?: number;
}
const DEFAULT_GROUP = [
  {
    field: 'serviceGroupName',
  },
];
const mergeDataItems = (array1: any[], array2: any[], key: string): any[] => {
  const mappedArray = new Map();
  Array.isArray(array1) &&
    array1?.forEach((item) => {
      mappedArray.set(item[key], item);
    });

  Array.isArray(array2) &&
    array2?.forEach((item) => {
      mappedArray.set(item[key], item);
    });

  return Array.from(mappedArray.values());
};

export const ServicesForm = ({ name, page, id }: ServicesFormProps) => {
  const isDisableEditableColumn = useAuthorize([UserPermissionName.EDIT_SREVICE_PRICE_CUSTOMER]);
  const isDisableCheckBoxBasedOnPermission = useAuthorize([UserPermissionName.DELETE_SERVICE_CUSTOMER]);
  const { setValue, getValues, watch } = useFormContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [initialService, setInitilalService] = useState<Service[]>(watch(name));

  const customColumns: GridColumnConfig[] = [
    {
      title: 'Invoice Amount ($)', // Main heading
      field: '', // Leave empty as it is a group header
      filter: 'text',
      isHideColumnMenu: true,
      children: [
        {
          title: 'Service',
          field: 'price',
          filter: 'text',
          fieldType: 'currency',
          editable: true,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
        },
        {
          title: 'Retest',
          field: 'invoiceRetestAmount',
          filter: 'text',
          fieldType: 'currency',
          editable: true,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
        },
      ],
    },
    {
      title: 'Accrual Amount ($)', // Main heading for accrual
      field: '', // Leave empty as it is a group header
      filter: 'text',
      isHideColumnMenu: true,
      children: [
        {
          title: 'Service',
          field: 'accrualAmount',
          filter: 'text',
          fieldType: 'currency',
          editable: true,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
        },
        {
          title: 'Retest',
          field: 'accrualRetestAmount',
          filter: 'text',
          fieldType: 'currency',
          editable: true,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
        },
      ],
    },
  ];

  const servicesEditableColumnsWithPermission: GridColumnConfig[] = [...servicesEditableColumns, ...customColumns];

  const handleOpenSelectServiceDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseSelectServiceDialog = (services: Service[]) => {
    setOpenDialog(false);
    const currentServices = getValues(name)?.filter((cs: Service) =>
      services?.some((s) => s.serviceID === cs.serviceID)
    );

    setValue(name, mergeDataItems(services, currentServices ?? [], 'serviceID'));
    setInitilalService(mergeDataItems(services, currentServices ?? [], 'serviceID'));
  };

  const handleCancelClick = () => {
    setOpenDialog(false);
  };

  const conditionalCellRender = (
    defaultRendering: React.ReactElement<HTMLTableCellElement> | null,
    props: GridCellProps
  ) => {
    if (props.field === SELECTED_FIELD && props.dataItem?.isRetestService) {
      return (
        <td>
          <Checkbox checked={props.dataItem?.isSelected} disabled={props.dataItem?.isRetestService} />
        </td>
      );
    } else if (
      props.field === SELECTED_FIELD &&
      props.dataItem?.mappingID !== 0 &&
      !isDisableCheckBoxBasedOnPermission
    ) {
      return (
        <Tooltip title={<Typography variant="body1">You do not have permission for delete</Typography>}>
          <td>
            <Checkbox checked={props.dataItem?.isSelected} disabled={true} />
          </td>
        </Tooltip>
      );
    } else {
      return defaultRendering;
    }
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout(!page ? 'unKnown' : page, name));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredService, setFilteredService] = useState<Service[]>(watch(name));

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const services = watch(name);

  useEffect(() => {
    const filteredServiceList = initialService
      ?.filter((d: Service) => !d.isNotReadyService)
      .filter((service: Service) =>
        Object.values(service).some(
          (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        )
      );
    setFilteredService(filteredServiceList || []);
  }, [debouncedSearchTerm]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: name,
      pageName: !page ? 'unKnown' : page,
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: name,
      pageName: !page ? 'unKnown' : page,
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  const areArraysEqual = (arr1: Service[], arr2: Service[]) => {
    return arr1.every((item, index) => item?.serviceID === arr2[index]?.serviceID);
  };

  useEffect(() => {
    if (!services || services.length === 0) {
      return;
    }
    if (!areArraysEqual(services, filteredService)) {
      setFilteredService(services?.filter((d: Service) => !d.isNotReadyService));
    }
  }, [services]);

  return (
    <Card elevation={0}>
      <ServiceNotReadyFields />
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary">
            SERVICES
          </Typography>
        }
        action={
          <GuardedComponent requiredPermissions={[UserPermissionName.ADD_SERVICE_CUSTOMER]}>
            <Button
              className="btnLink"
              variant="text"
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={handleOpenSelectServiceDialog}
            >
              Add New Service
            </Button>
          </GuardedComponent>
        }
      ></CardHeader>
      <CardContent className="formContainer">
        <Grid container>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <FormGridEditable
              fileTitle='customer_service'
              // defaultGroup={DEFAULT_GROUP}
              dataItemKey="serviceID"
              columns={servicesEditableColumnsWithPermission}
              name={name}
              isLoading={false}
              // dataItems={getValues(name)?.filter((d: Service) => !d.isNotReadyService)}
              // -----------------Grid Layout-------
              dataItems={filteredService || []}
              reorderable
              handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
            // -----------------Grid Layout-------
            />
          </Grid>
        </Grid>
        {openDialog && (
          <SelectServicesDialog
            open={openDialog}
            isNotReadyService
            onClose={handleCloseSelectServiceDialog}
            services={getValues(name) ?? []}
            conditionalCellRender={conditionalCellRender}
            handleCancelClick={handleCancelClick}
            isShowUncheckConfirmation={true}
            selectedField="isSelected"
            preventCheckAndUnCheckOnHeaderelectionChange={['isRetestService']}
            // selectedField={isDisableCheckBoxBasedOnPermission ? 'isSelected' : undefined}
            // servicesApiQuery={() => servicesApiKeyStore.getServices()}
            servicesApiQuery={() =>
              servicesApiKeyStore.getServiceMappingList(
                page === 'customer' ? id : 0,
                page === 'office' ? id : 0,
                page === 'project' ? id : 0,
                page === 'unit' ? id : 0,
                page === 'municipality' ? id : 0
              )
            }
          />
        )}
      </CardContent>
    </Card>
  );
};
